import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const TourBookings = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/tours/my-bookings`, { withCredentials: true });
      setBookings(response.data);
    } catch (error) {
      toast.error("Failed to fetch tour bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (bookingId, status) => {
    try {
      await axios.put(`${API}/tours/${bookingId}/status`, { status }, { withCredentials: true });
      toast.success(`Tour ${status}`);
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const filteredBookings = bookings.filter(b => {
    if (filter === "all") return true;
    return b.status === filter;
  });

  const upcomingCount = bookings.filter(b => b.status === "confirmed" && new Date(b.tour_date) >= new Date()).length;
  const todayCount = bookings.filter(b => {
    const today = new Date().toISOString().split('T')[0];
    return b.tour_date === today && b.status === "confirmed";
  }).length;

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="tour-bookings-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif">Tour Bookings</h2>
          <p className="text-muted-foreground text-sm">Manage scheduled property tours</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{todayCount}</p>
              <p className="text-sm text-muted-foreground">Today's Tours</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{upcomingCount}</p>
              <p className="text-sm text-muted-foreground">Upcoming</p>
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-2xl font-semibold">{bookings.filter(b => b.status === "completed").length}</p>
              <p className="text-sm text-muted-foreground">Completed</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        {["all", "confirmed", "completed", "cancelled"].map((f) => (
          <Button
            key={f}
            variant={filter === f ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        {filteredBookings.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tour bookings found</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredBookings.map((booking) => {
              const isToday = booking.tour_date === new Date().toISOString().split('T')[0];
              
              return (
                <div key={booking.booking_id} className={`p-4 ${isToday ? 'bg-primary/5' : ''}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{booking.customer_name}</span>
                        <Badge variant={
                          booking.status === "confirmed" ? "default" : 
                          booking.status === "completed" ? "secondary" : "destructive"
                        }>
                          {booking.status}
                        </Badge>
                        {isToday && <Badge variant="outline" className="text-primary border-primary">Today</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-xs mr-2">{booking.property_ref || booking.property_id}</span>
                        {booking.property_title}
                        {(user?.role === "admin" || user?.role === "branch_manager") && booking.agent_name && (
                          <span className="ml-2 text-primary">&#8226; Agent: {booking.agent_name}</span>
                        )}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(booking.tour_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(booking.tour_time)}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm mt-2">
                        <a href={`mailto:${booking.customer_email}`} className="flex items-center gap-1 text-primary hover:underline">
                          <Mail className="w-4 h-4" />
                          {booking.customer_email}
                        </a>
                        {booking.customer_phone && (
                          <a href={`tel:${booking.customer_phone}`} className="flex items-center gap-1 text-primary hover:underline">
                            <Phone className="w-4 h-4" />
                            {booking.customer_phone}
                          </a>
                        )}
                      </div>
                      {booking.notes && (
                        <p className="text-sm text-muted-foreground mt-2 italic">"{booking.notes}"</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {booking.status === "confirmed" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleStatusUpdate(booking.booking_id, "completed")}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleStatusUpdate(booking.booking_id, "cancelled")}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TourBookings;
